import { usePublishedHero } from 'hooks/usePublishedHero';
import PageIntroBand from 'components/PageIntroBand';
import { FlowButton } from 'components/ui/flow-button';
import { resolveCourseDetailPresentation } from 'lib/coursePresentation';
import { watchCourseContentChanges } from 'lib/courseContentChanges';
import 'components/CoursePresentation.css';
import './CoursesPage.css';
import HeroBackground from 'components/HeroBackground';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Loader2,
  Pause,
  Play,
  Share2,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useReveal } from 'hooks/useReveal';
import { useSiteContent } from 'hooks/useSiteContent';
import HeroScrollRail from 'components/HeroScrollRail';
import SiteHeroCtas, {
  HERO_CTA_PRIMARY_CLASS,
  HERO_CTA_SECONDARY_CLASS,
  PAGE_HERO_BODY_TEXT_CLASS,
  PAGE_HERO_CONTAINER_CLASS,
  PAGE_HERO_CTA_ROW_CLASS,
  PAGE_HERO_EYEBROW_CLASS,
  PAGE_HERO_H1_CLASS,
} from 'components/SiteHeroCtas';
import {
  captureWebinarPayPalOrder,
  confirmWebinarPaymentStatus,
  createWebinarPayPalOrder,
  createWebinarStripeCheckoutSession,
  extractCarrierFlowReference,
  loadWebinarPaymentConfig,
  startWebinarSwiftPayment,
  submitWebinarRegistration,
  WEBINAR_AGREEMENT_ACKNOWLEDGMENT,
  WEBINAR_AGREEMENT_TITLE,
  WEBINAR_AGREEMENT_VERSION,
  getWebinarAgreementUrl,
} from 'lib/carrierFlowApi';
import { reportClientError } from 'lib/clientErrorReporter';
import { DEFAULT_FEATURED_WEBINAR_CONTENT, getPublicFeaturedWebinarContent } from 'lib/webinarCms';
import { splitSiteContentParagraphs } from 'lib/siteContentCms';
import { toast } from 'sonner';
import { COURSE_CATEGORIES, listPublicCourses } from 'lib/siteCoursesApi';

const WEBINAR_CAL_URL = String(
  // L8: the hard-coded personal booking link is switched off; booking links are set in admin (Shop settings).
  process.env.REACT_APP_CALCOM_WEBINAR_EVENT_URL || ''
).trim();
const WEBINARS_HERO_SECTION_CLASS = 'relative flex min-h-[100svh] w-full items-center overflow-hidden md:h-screen md:min-h-[680px]';
const WEBINARS_MARQUEE_ITEMS = [
  "Driver records",
  "Audit document organization",
  "DataQs",
  "Safety follow-up",
  "SOPs",
  "Driver onboarding",
  "Recruiting",
  "TMS handoffs",
  "Management review",
];

function renderGoldHeroTitle(title) {
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  const goldStartIndex = Math.max(0, words.length - 3);

  return words.map((word, index) => (
    <span key={`${word}-${index}`} aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
      <span className={`inline-block ${index >= goldStartIndex ? 'italic shimmer-text' : ''}`}>
        {word}
      </span>
    </span>
  ));
}
const DATAQS_WEBINAR_ID = 'dataqs-ticket-challenge';
const SERIES_BUNDLE_ID = 'complete-webinar-series';
const SERIES_COLLAPSED_COUNT = 3;
const WEBINAR_CHECKOUT_SESSION_KEY = 'magellan_webinar_checkout';
const WEBINAR_CHECKOUT_SESSION_TTL_MS = 30 * 60 * 1000;
const WEBINAR_RETURN_PAYMENT_PROVIDERS = new Set(['paypal', 'stripe']);
const BASE_ALLOWED_PAYPAL_HOSTS = new Set([
  'paypal.com',
  'www.paypal.com',
  'sandbox.paypal.com',
  'www.sandbox.paypal.com',
]);
const BASE_ALLOWED_STRIPE_HOSTS = new Set([
  'checkout.stripe.com',
  'billing.stripe.com',
]);

const ATTENDEE_TYPES = [
  { value: 'company', label: 'Carrier / Fleet Company' },
  { value: 'employee', label: 'Employee / Team Member' },
  { value: 'broker', label: 'Freight Broker' },
  { value: 'other', label: 'Other / Independent' },
];

const EMPTY_WEBINAR_FORM = {
  name: '',
  email: '',
  phone: '',
  company: '',
  attendeeType: 'company',
  dotNumber: '',
  mcNumber: '',
  truckCount: '',
  employeeCount: '',
  companyZipCode: '',
  companyAddress: '',
  roleOrTitle: '',
  termsAccepted: false,
  agreementAcceptedAt: '',
  agreementVersion: WEBINAR_AGREEMENT_VERSION,
  agreementTitle: WEBINAR_AGREEMENT_TITLE,
  agreementUrl: '',
};

const WEBINAR_SERIES = [
  {
    number: '01',
    anchor: 'webinar-01-driver-files',
    title: 'Driver Files & Onboarding Control',
    shortText: 'How to organize driver files so missing documents, expired items, and onboarding gaps do not become last-minute problems.',
    covered: [
      'What belongs in a driver file',
      'How missing items usually happen',
      'Why onboarding and file control should work together',
      'How to review expired or incomplete documents',
      'How to create a simple driver file review rhythm',
    ],
    audience: 'Owners, safety managers, compliance staff, admin support, and anyone responsible for driver onboarding.',
    takeaway: 'You will understand how to make driver files easier to review, update, and control.',
  },
  {
    number: '02',
    anchor: 'webinar-02-dot-audit-readiness',
    title: 'DOT Audit Readiness & Compliance Folder Structure',
    shortText: 'How to prepare before audit pressure starts by organizing records, folders, ownership, and review routines.',
    covered: [
      'Why audit preparation should not begin after the notice arrives',
      'How to structure compliance folders',
      'What records usually create confusion',
      'How to assign ownership before documents go missing',
      'How to create a basic audit-readiness review',
    ],
    audience: 'Owners, compliance staff, safety managers, and operations leaders.',
    takeaway: 'You will know how to organize records so the company is not scrambling under pressure.',
  },
  {
    number: '03',
    anchor: 'webinar-03-dataqs-inspection-follow-up',
    registrationId: DATAQS_WEBINAR_ID,
    title: 'DataQs & Inspection Follow-Up',
    shortText: 'How to review inspection issues, collect supporting information, and keep follow-up from disappearing into memory.',
    covered: [
      'What to document after an inspection',
      'How to review inspection details internally',
      'What information may be needed before a DataQs review',
      'How inspection follow-up gets lost',
      'How to track open inspection issues',
    ],
    audience: 'Safety managers, owners, dispatch managers, and anyone involved in inspection follow-up.',
    takeaway: 'You will understand how to create a cleaner inspection-review process.',
  },
  {
    number: '04',
    anchor: 'webinar-04-safety-meetings-incident-review',
    title: 'Safety Meetings & Incident Review',
    shortText: 'How to turn safety conversations into documented review, assigned follow-up, and better management visibility.',
    covered: [
      'How to structure a useful safety meeting',
      'What should be documented',
      'How to review incidents without creating confusion',
      'How to assign follow-up',
      'How to track unresolved safety items',
    ],
    audience: 'Safety managers, owners, driver managers, dispatchers, and operations leaders.',
    takeaway: 'You will know how to make safety meetings more practical and less reactive.',
  },
  {
    number: '05',
    anchor: 'webinar-05-sops-internal-workflows',
    title: 'SOPs & Internal Workflows',
    shortText: 'How to document the way work should happen without creating binders nobody uses.',
    covered: [
      'What makes an SOP useful',
      'Which processes should be written first',
      'How to document handoffs',
      'How to reduce everyone does it differently',
      'How to keep SOPs practical and current',
    ],
    audience: 'Owners, operations managers, dispatch leaders, admin staff, and department leads.',
    takeaway: 'You will understand how to write procedures that people can actually follow.',
  },
  {
    number: '06',
    anchor: 'webinar-06-driver-onboarding-training',
    title: 'Driver Onboarding & Training Rhythm',
    shortText: 'How to make training repeatable so new drivers are not learning the company by guessing.',
    covered: [
      'What should happen before first dispatch',
      'How to structure onboarding steps',
      'How to repeat training without overcomplicating it',
      'How to document training completion',
      'How to build a basic refresher rhythm',
    ],
    audience: 'Owners, safety teams, recruiters, driver managers, and training coordinators.',
    takeaway: 'You will know how to make driver training more consistent and easier to repeat.',
  },
  {
    number: '07',
    anchor: 'webinar-07-recruiting-retention',
    title: 'Recruiting Pipeline & Driver Retention',
    shortText: 'How to organize recruiting activity so applicants, follow-up, onboarding, and retention do not depend on memory.',
    covered: [
      'How recruiting leads get lost',
      'What a simple driver pipeline should track',
      'How to organize follow-up',
      'How recruiting connects to onboarding',
      'How to review why drivers stay or leave',
    ],
    audience: 'Recruiters, owners, operations managers, and anyone involved in hiring drivers.',
    takeaway: 'You will understand how to move from scattered recruiting activity to pipeline control.',
  },
  {
    number: '08',
    anchor: 'webinar-08-tms-workflows',
    title: 'TMS Workflows & Dispatch Consistency',
    shortText: 'How to make TMS usage more consistent so the company can trust the information inside the system.',
    covered: [
      'Why software does not create structure by itself',
      'Where TMS usage becomes inconsistent',
      'What fields and steps should be standardized',
      'How dispatch handoffs affect data quality',
      'How to review whether the system reflects reality',
    ],
    audience: 'Dispatchers, operations managers, owners, admin staff, and TMS users.',
    takeaway: 'You will know how to review TMS usage and identify where the workflow needs standardization.',
  },
  {
    number: '09',
    anchor: 'webinar-09-management-visibility',
    title: 'Management Visibility & Open Items Review',
    shortText: 'How owners and managers can see what is open, late, missing, unresolved, or owned by nobody.',
    covered: [
      'Why owners often find out too late',
      'What open items should be tracked',
      'How to create a basic review rhythm',
      'How dashboards should support decisions',
      'How to connect records, safety, recruiting, and operations visibility',
    ],
    audience: 'Owners, managers, safety leaders, operations leaders, and department heads.',
    takeaway: 'You will understand what leadership needs to see before problems become urgent.',
  },
  {
    number: '10',
    anchor: 'webinar-10-operating-system',
    title: 'Building the Operating System Behind the Company',
    shortText: 'How the pieces connect: files, training, safety, recruiting, TMS workflows, dashboards, and management review.',
    covered: [
      'Why isolated fixes do not solve the whole problem',
      'How the operating pieces connect',
      'How to choose what to fix first',
      'How to avoid overwhelming the team',
      'How to build structure that survives a busy week',
    ],
    audience: 'Owners, executives, operations managers, safety leaders, and anyone responsible for company-wide structure.',
    takeaway: 'You will understand how to move from scattered fixes to one connected operating system.',
  },
];

const SERIES_REACH_WEBINAR_IDS = new Set(
  WEBINAR_SERIES.reduce((ids, webinar) => {
    ids.push(webinar.anchor);
    if (webinar.registrationId) ids.push(webinar.registrationId);
    return ids;
  }, [SERIES_BUNDLE_ID])
);

const SERIES_INTRO_LINES = [
  "A driver record with missing information or an expiration date to check.",
  "An inspection question that still needs supporting information.",
  "A Safety meeting with decisions and actions to follow up.",
  "A new driver who needs initial instruction and a plan for later refreshers.",
  "An applicant waiting for the next contact.",
  "A load update or document the next dispatcher needs.",
];

const STARTING_GUIDE = [
  ["Driver file checks & recordkeeping", "Check collected driver documents and follow up on missing or incomplete information."],
  ["DOT Audit Document Organization", "Organize documents for Department of Transportation (DOT) audit preparation so the team can find records and see which need updating."],
  ["DataQs & Inspection Record Review", "Review inspection data and organize supporting information for a possible DataQs request."],
  ["Safety Meetings & Incident Follow-Up", "Record meeting decisions, unanswered incident questions and the follow-up assigned."],
  ["SOPs & Internal Workflows", "Write standard operating procedures (SOPs) for recurring tasks and explain what passes to the next person."],
  ["Driver Onboarding & Refresher Training", "Plan initial driver instruction, record completed learning and arrange later refreshers."],
  ["Driver recruiting follow-up", "Track applicant contact through the handoff to onboarding, keeping unanswered questions and unfinished requests visible."],
  ["TMS Workflows & Dispatch Handoffs", "Review load updates, documents and dispatch handoffs in a transportation management system (TMS), including their use in CarrierFlow TMS."],
  ["Management Visibility & Open Items Review", "Bring overdue records, unanswered questions and pending decisions into a regular management review."],
  ["Mapping a trucking company process", "Map one process across departments, identify where work stalls and decide which improvements to make first."],
];

const ALLOWED_WEBINAR_BOOKING_HOSTS = new Set(
  String(
    process.env.REACT_APP_CALCOM_ALLOWED_HOSTS
    || process.env.REACT_APP_TRUSTED_CALCOM_HOSTS
    || ''
  )
    .split(',')
    .map((host) => sanitizeText(host).toLowerCase())
    .filter(Boolean)
);

function sanitizeText(value) {
  return String(value || '').trim();
}

function toAnchorSegment(value, fallback = 'webinar') {
  const segment = sanitizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 90);

  return segment || fallback;
}

function getPublicWebinarAnchor(webinar) {
  return `webinar-session-${toAnchorSegment(webinar?.id || webinar?.slug || webinar?.title)}`;
}

function getCourseDetailPath(webinar) {
  return `/webinars/${webinar?.slug || toAnchorSegment(webinar?.id || webinar?.registrationId || webinar?.anchor || webinar?.title, 'course')}`;
}

function normalizeMoney(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }
  return Number(numeric.toFixed(2));
}

function formatMoney(amount = 0, currency = 'USD') {
  if (!amount) return 'Free';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${currency} ${amount}`;
  }
}

function sanitizeWebinarPaymentProvider(value, fallback = '') {
  const provider = sanitizeText(value).toLowerCase();
  return ['manual', 'paypal', 'stripe', 'swift'].includes(provider) ? provider : fallback;
}

function sanitizeWebinarReturnPaymentProvider(value) {
  const provider = sanitizeText(value).toLowerCase();
  return WEBINAR_RETURN_PAYMENT_PROVIDERS.has(provider) ? provider : '';
}

function getWebinarPaymentConfig(webinar) {
  const hasPublishedAmount = [
    webinar?.paymentAmount,
    webinar?.priceAmount,
    webinar?.amount,
    webinar?.price,
  ].some((value) => normalizeMoney(value) > 0);
  const amount = normalizeMoney(
    webinar?.paymentAmount
    ?? webinar?.priceAmount
    ?? webinar?.amount
    ?? webinar?.price
    ?? 0
  );
  const currency = sanitizeText(webinar?.paymentCurrency || webinar?.currency || 'USD') || 'USD';
  const hasExplicitProvider = Object.prototype.hasOwnProperty.call(webinar || {}, 'paymentProvider')
    && Boolean(sanitizeText(webinar?.paymentProvider));
  const provider = sanitizeWebinarPaymentProvider(webinar?.paymentProvider, amount > 0 ? '' : 'manual');
  const providerLabel = hasExplicitProvider && provider
    ? getPaymentProviderLabel(provider)
    : 'configured payment method';
  return {
    amount,
    currency,
    required: Boolean(webinar?.paymentRequired) || amount > 0,
    provider: provider || 'manual',
    providerLabel,
    providerConfigured: hasExplicitProvider,
    ready: !amount || Boolean(provider),
    label: hasPublishedAmount ? formatMoney(amount, currency) : 'Approved CMS/product price',
  };
}

function getPaymentProviderLabel(provider) {
  switch (sanitizeText(provider).toLowerCase()) {
    case 'stripe':
      return 'Stripe';
    case 'swift':
      return 'SWIFT';
    case 'paypal':
      return 'PayPal';
    default:
      return 'Manual review';
  }
}

function getSubmitButtonLabel(payment, isInterestOnly) {
  if (payment.required) {
    return payment.provider === 'swift' ? 'Get SWIFT Details' : `Continue to ${payment.providerLabel}`;
  }
  return isInterestOnly ? 'Submit Interest' : 'Confirm Free Registration';
}

function mergePaymentConfigForWebinar(basePayment, paymentConfig) {
  if (!paymentConfig || typeof paymentConfig !== 'object') {
    return basePayment;
  }

  const amount = normalizeMoney(paymentConfig.paymentAmount ?? paymentConfig.amount ?? basePayment.amount);
  const currency = sanitizeText(paymentConfig.paymentCurrency || paymentConfig.currency || basePayment.currency || 'USD') || 'USD';
  const provider = sanitizeWebinarPaymentProvider(
    paymentConfig.paymentProvider || paymentConfig.provider?.provider || basePayment.provider,
    basePayment.provider || 'manual'
  );
  const providerLabel = sanitizeText(paymentConfig.provider?.displayName) || getPaymentProviderLabel(provider);
  const hasRequiredFlag = Object.prototype.hasOwnProperty.call(paymentConfig, 'paymentRequired');
  const required = hasRequiredFlag ? Boolean(paymentConfig.paymentRequired) : Boolean(basePayment.required || amount > 0);
  const ready = !required || Boolean(paymentConfig.readiness?.ready || paymentConfig.provider?.ready);

  return {
    ...basePayment,
    amount,
    currency,
    required,
    provider,
    providerLabel,
    providerConfigured: true,
    ready,
    unavailableReason: sanitizeText(paymentConfig.readiness?.unavailableReason || paymentConfig.provider?.unavailableReason, 120),
    label: formatMoney(amount, currency),
  };
}

function getPayPalApprovalUrl(data) {
  if (!data || typeof data !== 'object') return '';
  return [
    data.approvalUrl,
    data.approveUrl,
    data.paypalApprovalUrl,
    data.redirectUrl,
    data.url,
    data.links?.find?.((link) => String(link.rel || '').toLowerCase() === 'approve')?.href,
  ].find((value) => sanitizeText(value)) || '';
}

function getPayPalOrderReference(data) {
  if (!data || typeof data !== 'object') return '';
  return [
    data.orderId,
    data.orderID,
    data.id,
    data.paypalOrderId,
    data.order?.id,
  ].find((value) => sanitizeText(value)) || '';
}

function getCaptureReference(data) {
  if (!data || typeof data !== 'object') return '';
  return [
    data.captureId,
    data.paypalCaptureId,
    data.id,
    data.capture?.id,
    data.payment?.captureId,
  ].find((value) => sanitizeText(value)) || '';
}

function getCheckoutUrl(data) {
  if (!data || typeof data !== 'object') return '';
  return [
    data.checkoutUrl,
    data.url,
    data.redirectUrl,
    data.session?.url,
  ].find((value) => sanitizeText(value)) || '';
}

function getCheckoutSessionReference(data) {
  if (!data || typeof data !== 'object') return '';
  return [
    data.checkoutSessionId,
    data.sessionId,
    data.session?.id,
  ].find((value) => sanitizeText(value)) || '';
}

function isTrustedPayPalUrl(rawUrl) {
  try {
    const parsed = new URL(sanitizeText(rawUrl));
    return parsed.protocol === 'https:' && BASE_ALLOWED_PAYPAL_HOSTS.has(parsed.hostname.toLowerCase());
  } catch (error) {
    return false;
  }
}

function isTrustedStripeUrl(rawUrl) {
  try {
    const parsed = new URL(sanitizeText(rawUrl));
    return parsed.protocol === 'https:' && BASE_ALLOWED_STRIPE_HOSTS.has(parsed.hostname.toLowerCase());
  } catch (error) {
    return false;
  }
}

function sanitizeWebinarCheckoutSession(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const expiresAt = Number(raw.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt < Date.now()) return null;
  const provider = sanitizeWebinarReturnPaymentProvider(raw.provider);
  if (!provider) return null;
  return {
    provider,
    registrationReference: sanitizeText(raw.registrationReference),
    checkoutSessionId: sanitizeText(raw.checkoutSessionId),
    orderId: sanitizeText(raw.orderId),
    form: raw.form && typeof raw.form === 'object' ? raw.form : {},
    webinar: raw.webinar && typeof raw.webinar === 'object'
      ? {
        id: sanitizeText(raw.webinar.id),
        title: sanitizeText(raw.webinar.title),
      }
      : {},
    payment: raw.payment && typeof raw.payment === 'object'
      ? {
        amount: normalizeMoney(raw.payment.amount),
        currency: sanitizeText(raw.payment.currency || 'USD') || 'USD',
        provider: sanitizeWebinarPaymentProvider(raw.payment.provider, provider),
      }
      : {},
    paymentConfig: raw.paymentConfig && typeof raw.paymentConfig === 'object' ? raw.paymentConfig : undefined,
    expiresAt,
  };
}

function readCheckoutSession() {
  try {
    const raw = window.sessionStorage.getItem(WEBINAR_CHECKOUT_SESSION_KEY);
    if (!raw) return null;
    const session = sanitizeWebinarCheckoutSession(JSON.parse(raw));
    if (!session) clearCheckoutSession();
    return session;
  } catch (error) {
    clearCheckoutSession();
    return null;
  }
}

function writeCheckoutSession(payload) {
  try {
    const session = sanitizeWebinarCheckoutSession({
      ...payload,
      expiresAt: Date.now() + WEBINAR_CHECKOUT_SESSION_TTL_MS,
    });
    if (!session) {
      throw new Error('Invalid webinar checkout session payload.');
    }
    window.sessionStorage.setItem(WEBINAR_CHECKOUT_SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('Failed to store webinar checkout session:', error);
  }
}

function clearCheckoutSession() {
  try {
    window.sessionStorage.removeItem(WEBINAR_CHECKOUT_SESSION_KEY);
  } catch (error) {
    console.error('Failed to clear webinar checkout session:', error);
  }
}

function isTrustedWebinarBookingHost(hostname) {
  const normalized = sanitizeText(hostname).toLowerCase().replace(/^\./, '');
  if (!normalized) {
    return false;
  }
  return (
    normalized === 'cal.com'
    || normalized.endsWith('.cal.com')
    || ALLOWED_WEBINAR_BOOKING_HOSTS.has(normalized)
  );
}

function buildTrustedWebinarBookingUrl(form) {
  try {
    const url = new URL(WEBINAR_CAL_URL);
    const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(url.hostname.toLowerCase());

    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLocalhost)) {
      return '';
    }

    if (!isTrustedWebinarBookingHost(url.hostname)) {
      return '';
    }

    if (form.name) url.searchParams.set('name', form.name);
    if (form.email) url.searchParams.set('email', form.email);
    if (form.company) url.searchParams.set('company', form.company);
    return url.toString();
  } catch (error) {
    return '';
  }
}

function parseWebinarDateTimestamp(dateValue) {
  if (!dateValue) {
    return null;
  }

  try {
    const ts = dateValue._seconds
      ? dateValue._seconds * 1000
      : typeof dateValue?.toDate === 'function'
        ? dateValue.toDate().getTime()
        : typeof dateValue === 'string' || typeof dateValue === 'number'
          ? new Date(dateValue).getTime()
          : null;

    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function formatWebinarDate(dateValue) {
  const ts = parseWebinarDateTimestamp(dateValue);
  if (!ts) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(new Date(ts));
  } catch {
    return '';
  }
}

function formatWebinarDateOnly(dateValue) {
  const ts = parseWebinarDateTimestamp(dateValue);
  if (!ts) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    }).format(new Date(ts));
  } catch {
    return '';
  }
}

function getWebinarScheduleLabel(webinar) {
  const dateValue = (
    webinar?.startsAt
    || webinar?.startAt
    || webinar?.scheduledAt
    || webinar?.date
    || webinar?.startDate
    || webinar?.eventDate
  );
  const convertedDate = formatWebinarDate(dateValue);
  if (convertedDate) {
    return convertedDate;
  }

  const explicit = sanitizeText(
    webinar?.dateTime
    || webinar?.scheduleLabel
    || webinar?.scheduledFor
    || webinar?.sessionTime
  );
  if (explicit) {
    return explicit;
  }

  const timeLabel = sanitizeText(webinar?.time || webinar?.startTime || webinar?.timeLabel);
  const dateOnly = formatWebinarDateOnly(dateValue);

  if (dateOnly && timeLabel) {
    return `${dateOnly} - ${timeLabel}`;
  }

  return timeLabel || 'Date and time to be announced';
}

function isTrustedYouTubePreviewEmbedUrl(embedUrl) {
  try {
    const url = new URL(sanitizeText(embedUrl));
    return (
      url.protocol === 'https:'
      && url.hostname.toLowerCase() === 'www.youtube-nocookie.com'
      && /^\/embed\/[A-Za-z0-9_-]{11}$/.test(url.pathname)
    );
  } catch {
    return false;
  }
}

function isTrustedPublicVideoUrl(videoUrl) {
  try {
    const url = new URL(sanitizeText(videoUrl));
    return url.protocol === 'https:';
  } catch {
    return false;
  }
}

function parsePublicYouTubeVideoId(value) {
  const candidate = sanitizeText(value);
  if (/^[A-Za-z0-9_-]{11}$/.test(candidate)) {
    return candidate;
  }

  try {
    const url = new URL(candidate);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0] || '';
      return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : '';
    }
    if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'm.youtube.com') {
      const watchId = url.searchParams.get('v') || '';
      if (/^[A-Za-z0-9_-]{11}$/.test(watchId)) return watchId;
      const parts = url.pathname.split('/').filter(Boolean);
      const markerIndex = parts.findIndex((part) => ['embed', 'shorts', 'live'].includes(part));
      const id = markerIndex >= 0 ? parts[markerIndex + 1] || '' : '';
      return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : '';
    }
  } catch {
    return '';
  }

  return '';
}

function getPublicYouTubePlayerUrl(value, shouldAutoplay) {
  const videoId = parsePublicYouTubeVideoId(value);
  if (!videoId) return '';
  const params = new URLSearchParams({
    controls: '0',
    disablekb: '1',
    enablejsapi: '1',
    fs: '0',
    modestbranding: '1',
    playsinline: '1',
    rel: '0',
  });
  if (shouldAutoplay) {
    params.set('autoplay', '1');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    params.set('origin', window.location.origin);
  }
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}

function hasPublicWebinarPreview(webinar) {
  if (!webinar?.previewVideoEnabled) return false;
  const provider = sanitizeText(webinar.previewVideoProvider).toLowerCase();
  if (provider === 'youtube') {
    return isTrustedYouTubePreviewEmbedUrl(webinar.previewYoutubeEmbedUrl);
  }
  if (provider === 'upload') {
    return isTrustedPublicVideoUrl(webinar.previewVideoUrl);
  }
  return false;
}

function getYouTubePreviewPlayerUrl(embedUrl, shouldAutoplay) {
  if (!isTrustedYouTubePreviewEmbedUrl(embedUrl)) {
    return '';
  }
  try {
    const url = new URL(sanitizeText(embedUrl));
    if (shouldAutoplay) {
      url.searchParams.set('autoplay', '1');
    }
    url.searchParams.set('controls', '0');
    url.searchParams.set('disablekb', '1');
    url.searchParams.set('enablejsapi', '1');
    url.searchParams.set('fs', '1');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('rel', '0');
    return url.toString();
  } catch {
    return '';
  }
}

function postYouTubePreviewCommand(iframe, embedUrl, command, args = []) {
  if (!iframe?.contentWindow || !command) {
    return;
  }
  try {
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: command, args }),
      new URL(embedUrl).origin
    );
  } catch {
    iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: command, args }), '*');
  }
}

function pausePublicWebinarVideos(exceptVideo = null) {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('video[data-public-webinar-video="true"]').forEach((video) => {
    if (video !== exceptVideo && typeof video.pause === 'function') {
      video.pause();
    }
  });
}

function PublicVideoPlayer({
  src = '',
  posterUrl = '',
  title = 'Video preview',
  label = '',
  className = '',
  videoClassName = 'object-cover',
  autoPlay = false,
}) {
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const cleanSrc = sanitizeText(src);
  const youtubePlayerUrl = getPublicYouTubePlayerUrl(cleanSrc, autoPlay);
  const isYouTube = Boolean(youtubePlayerUrl);
  const [isStarted, setIsStarted] = useState(Boolean(autoPlay && isYouTube));
  const [isPlaying, setIsPlaying] = useState(Boolean(autoPlay && isYouTube));
  const [isMuted, setIsMuted] = useState(false);
  const cleanTitle = sanitizeText(title) || 'Video preview';
  const cleanLabel = sanitizeText(label);

  useEffect(() => {
    setIsStarted(Boolean(autoPlay && isYouTube));
    setIsPlaying(Boolean(autoPlay && isYouTube));
  }, [autoPlay, isYouTube, cleanSrc]);

  useEffect(() => {
    if (!isYouTube) return undefined;
    const iframe = iframeRef.current;
    return () => {
      postYouTubePreviewCommand(iframe, youtubePlayerUrl, 'pauseVideo');
    };
  }, [isYouTube, youtubePlayerUrl, isStarted]);

  useEffect(() => {
    if (isYouTube || !autoPlay || !cleanSrc) return undefined;
    const timer = window.setTimeout(() => {
      const video = videoRef.current;
      if (!video) return;
      pausePublicWebinarVideos(video);
      video.play().catch(() => setIsPlaying(false));
    }, 50);
    return () => window.clearTimeout(timer);
  }, [autoPlay, cleanSrc, isYouTube]);

  useEffect(() => {
    if (isYouTube && autoPlay && isStarted) {
      const timer = window.setTimeout(() => postYouTubePreviewCommand(iframeRef.current, youtubePlayerUrl, 'playVideo'), 350);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [autoPlay, isStarted, isYouTube, youtubePlayerUrl]);

  useEffect(() => {
    if (isYouTube) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    const syncPlaying = () => setIsPlaying(!video.paused && !video.ended);
    const syncMuted = () => setIsMuted(Boolean(video.muted));

    video.addEventListener('play', syncPlaying);
    video.addEventListener('pause', syncPlaying);
    video.addEventListener('ended', syncPlaying);
    video.addEventListener('volumechange', syncMuted);
    syncPlaying();
    syncMuted();

    return () => {
      video.removeEventListener('play', syncPlaying);
      video.removeEventListener('pause', syncPlaying);
      video.removeEventListener('ended', syncPlaying);
      video.removeEventListener('volumechange', syncMuted);
    };
  }, [cleanSrc, isYouTube]);

  useEffect(() => () => {
    videoRef.current?.pause?.();
  }, []);

  const handlePlayPause = () => {
    if (isYouTube) {
      if (!youtubePlayerUrl) return;
      if (!isStarted) {
        pausePublicWebinarVideos();
        setIsStarted(true);
        setIsPlaying(true);
        window.setTimeout(() => postYouTubePreviewCommand(iframeRef.current, youtubePlayerUrl, 'playVideo'), 350);
        return;
      }
      const nextPlaying = !isPlaying;
      postYouTubePreviewCommand(iframeRef.current, youtubePlayerUrl, nextPlaying ? 'playVideo' : 'pauseVideo');
      setIsPlaying(nextPlaying);
      return;
    }

    const video = videoRef.current;
    if (!video || !cleanSrc) return;

    if (video.paused || video.ended) {
      pausePublicWebinarVideos(video);
      video.play().catch(() => setIsPlaying(false));
      return;
    }
    video.pause();
  };

  const handleMuteToggle = () => {
    if (isYouTube) {
      const nextMuted = !isMuted;
      postYouTubePreviewCommand(iframeRef.current, youtubePlayerUrl, nextMuted ? 'mute' : 'unMute');
      setIsMuted(nextMuted);
      return;
    }

    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  return (
    <div data-theme-surface="dark" className={`relative aspect-video overflow-hidden border border-ink/10 bg-black ${className}`}>
      {isYouTube && isStarted ? (
        <iframe
          ref={iframeRef}
          className="absolute inset-0 h-full w-full bg-black"
          src={youtubePlayerUrl}
          title={cleanTitle}
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      ) : isYouTube ? (
        posterUrl ? (
          <img src={posterUrl} alt="" className={`absolute inset-0 h-full w-full bg-black ${videoClassName}`} loading="lazy" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gold/[0.16] via-canvas to-black" />
        )
      ) : (
        <video
          ref={videoRef}
          data-public-webinar-video="true"
          className={`absolute inset-0 h-full w-full bg-black ${videoClassName}`}
          preload="metadata"
          playsInline
          poster={posterUrl || undefined}
          src={cleanSrc || undefined}
          title={cleanTitle}
          controls={false}
          controlsList="nodownload noplaybackrate noremoteplayback"
          disablePictureInPicture
          disableRemotePlayback
          onContextMenu={(event) => event.preventDefault()}
        />
      )}
      {!cleanSrc ? (
        <div className="absolute inset-0 bg-gradient-to-br from-gold/[0.16] via-canvas to-black" />
      ) : null}
      {cleanLabel ? (
        <div className="absolute left-4 top-4 rounded-full border border-ink/14 bg-canvas/70 px-3 py-1">
          <p className="font-body text-[10px] uppercase tracking-[0.2em] text-copy/70">{cleanLabel}</p>
        </div>
      ) : null}
      {cleanSrc && !isPlaying ? (
        <button
          type="button"
          onClick={handlePlayPause}
          className="absolute left-1/2 top-1/2 inline-flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold/40 bg-gold text-obsidian shadow-[0_0_34px_rgba(76,141,204,0.2)] transition-transform hover:scale-105"
          aria-label="Play video"
        >
          <Play className="h-6 w-6 fill-current" />
        </button>
      ) : null}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={handlePlayPause}
          disabled={!cleanSrc}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 bg-canvas/82 text-accent-ink backdrop-blur transition-colors hover:bg-gold hover:text-obsidian disabled:cursor-not-allowed disabled:opacity-45"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
          {isPlaying ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
        </button>
        <button
          type="button"
          onClick={handleMuteToggle}
          disabled={!cleanSrc || (isYouTube && !isStarted)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 bg-canvas/82 text-copy/74 backdrop-blur transition-colors hover:border-gold/50 hover:text-accent-ink disabled:cursor-not-allowed disabled:opacity-45"
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
        >
          {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function formatPreviewDurationLabel(seconds) {
  const duration = Number(seconds);
  if (!Number.isFinite(duration) || duration <= 0) {
    return 'Preview';
  }
  const rounded = Math.round(duration);
  const minutes = Math.floor(rounded / 60);
  const remainingSeconds = rounded % 60;
  if (!minutes) {
    return `${remainingSeconds}s preview`;
  }
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')} preview`;
}

function copyToClipboard(text) {
  if (navigator?.clipboard?.writeText && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }

  const element = document.createElement('textarea');
  element.value = text;
  element.setAttribute('readonly', '');
  element.style.position = 'fixed';
  element.style.left = '-9999px';
  document.body.appendChild(element);
  element.select();
  document.execCommand('copy');
  document.body.removeChild(element);
  return Promise.resolve();
}

function normalizeLookupText(value) {
  return sanitizeText(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function splitReachList(value) {
  if (Array.isArray(value)) {
    return value.map(sanitizeText).filter(Boolean).slice(0, 8);
  }

  return sanitizeText(value)
    .split(/\n|;/)
    .map(sanitizeText)
    .filter(Boolean)
    .slice(0, 8);
}

function getReachList(webinar, keys = []) {
  for (const key of keys) {
    const lines = splitReachList(webinar?.[key]);
    if (lines.length) {
      return lines;
    }
  }
  return [];
}

function getReachWebinarMatchScore(seriesCard, liveWebinar) {
  const liveId = normalizeLookupText(liveWebinar?.id);
  const liveTitle = normalizeLookupText(liveWebinar?.title);
  const anchor = normalizeLookupText(seriesCard.anchor);
  const registrationId = normalizeLookupText(seriesCard.registrationId);
  const seriesTitle = normalizeLookupText(seriesCard.title);

  if (!liveId && !liveTitle) {
    return 0;
  }
  if ([anchor, registrationId].filter(Boolean).includes(liveId)) {
    return 100;
  }
  if (liveTitle === seriesTitle) {
    return 90;
  }
  if (liveTitle.includes(seriesTitle) || seriesTitle.includes(liveTitle)) {
    return 70;
  }

  const liveWords = new Set(liveTitle.split(' ').filter((word) => word.length > 3));
  const seriesWords = seriesTitle.split(' ').filter((word) => word.length > 3);
  return seriesWords.reduce((score, word) => score + (liveWords.has(word) ? 1 : 0), 0);
}

function isReachSeriesWebinar(webinar) {
  const liveId = sanitizeText(webinar?.id || webinar?.slug);
  if (liveId && SERIES_REACH_WEBINAR_IDS.has(liveId)) {
    return true;
  }
  if (Array.isArray(webinar?.bundleWebinarIds) && webinar.bundleWebinarIds.length > 0) {
    return true;
  }
  const webinarNumber = sanitizeText(webinar?.webinarNumber || webinar?.number);
  const sortOrder = Number(webinar?.sortOrder);
  if (/^(0?[1-9]|10)$/.test(webinarNumber) || (Number.isFinite(sortOrder) && sortOrder >= 1 && sortOrder <= 10)) {
    return true;
  }
  return WEBINAR_SERIES.some((seriesCard) => getReachWebinarMatchScore(seriesCard, webinar) >= 90);
}

function resolveSeriesCards(webinars = []) {
  return webinars
    .filter((webinar) => webinar.kind !== 'bundle')
    .sort((a, b) => {
      const order = value => Number.isSafeInteger(value) && value >= 0 ? value : Number.MAX_SAFE_INTEGER;
      const sortDelta = order(a.sortOrder) - order(b.sortOrder);
      if (sortDelta !== 0) return sortDelta;
      const numberDelta = (Number(a.webinarNumber || a.number) || Number.MAX_SAFE_INTEGER) - (Number(b.webinarNumber || b.number) || Number.MAX_SAFE_INTEGER);
      if (numberDelta !== 0) return numberDelta;
      return String(a.date || a.startAt || '').localeCompare(String(b.date || b.startAt || ''));
    })
    .map((webinar, index) => {
      const reachCovered = getReachList(webinar, ['covered', 'whatCovered', 'whatIsCovered', 'agenda', 'topics']);
      const webinarNumber = sanitizeText(webinar.webinarNumber || webinar.number)
        || (Number.isFinite(Number(webinar.sortOrder)) ? String(Number(webinar.sortOrder)).padStart(2, '0') : String(index + 1).padStart(2, '0'));
      const anchor = webinar.anchor || getPublicWebinarAnchor(webinar);

      return {
        id: webinar.id,
        slug: webinar.slug,
        category: webinar.category,
        publicTheme: webinar.publicTheme,
        accentColor: webinar.accentColor,
        themeLabel: webinar.themeLabel,
        visualTemplate: webinar.visualTemplate,
        isDemo: webinar.isDemo,
        enrollmentEnabled: false,
        registrationId: webinar.id,
        reachWebinarId: webinar.id,
        anchor,
        number: webinarNumber,
        title: sanitizeText(webinar.title),
        shortText: sanitizeText(webinar.description),
        covered: reachCovered,
        audience: sanitizeText(webinar.audience || webinar.whoShouldWatch),
        takeaway: sanitizeText(webinar.takeaway),
        price: normalizeMoney(webinar.paymentAmount ?? webinar.price),
        currency: sanitizeText(webinar.paymentCurrency || webinar.currency || 'USD') || 'USD',
        paymentRequired: Boolean(webinar.paymentRequired) || normalizeMoney(webinar.paymentAmount ?? webinar.price) > 0,
        paymentProvider: sanitizeWebinarPaymentProvider(webinar.paymentProvider, ''),
        scheduleLabel: getWebinarScheduleLabel(webinar),
        previewVideoEnabled: Boolean(webinar.previewVideoEnabled),
        previewVideoProvider: webinar.previewVideoProvider,
        previewYoutubeEmbedUrl: webinar.previewYoutubeEmbedUrl,
        previewVideoUrl: webinar.previewVideoUrl,
        previewVideoTitle: webinar.previewVideoTitle,
        previewVideoDurationSeconds: webinar.previewVideoDurationSeconds,
        previewCoverImageUrl: webinar.previewCoverImageUrl,
      };
    });
}

function findReachSeriesBundle(webinars = []) {
  return webinars.find((webinar) => webinar.kind === 'bundle') || null;
}

function SectionHeader({ eyebrow, title, copy, className = '' }) {
  return (
    <div className={className}>
      <p className="reveal text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">{eyebrow}</p>
      <h2 className="reveal reveal-delay-1 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.04] text-ink mb-6">{title}</h2>
      {copy ? <p className="reveal reveal-delay-2 font-body text-copy/54 leading-relaxed max-w-3xl">{copy}</p> : null}
    </div>
  );
}

function WebinarsIntroBand() {
  return (
    <PageIntroBand
      testId="webinars-page-intro"
      label="Learning for your responsibilities"
      title="Learn how trucking office responsibilities fit together."
      backgroundText="COURSES"
      paragraphs={[
        "Checking a driver’s documents is different from teaching company instructions. A manager reviewing overdue items also needs a different view from someone planning a change across departments.",
        "The courses connect individual tasks with the handoffs around them. Driver-file checks continue after onboarding, and written procedures explain when another person takes over.",
      ]}
    />
  );
}

function VideoPreview({
  videoUrl = '',
  fullVideoUrl = '',
  posterUrl = '',
  eyebrow = "Series overview",
  title = "What the courses cover",
  copy = "An introduction to the subjects and the people they are intended for.",
  className = '',
}) {
  const inlineSrc = videoUrl || fullVideoUrl;
  return (
    <div data-testid="courses-hero-video" className={`courses-hero-video courses-video-glass reveal reveal-delay-2 p-5 xl:p-6 ${className}`}>
      <PublicVideoPlayer
        src={inlineSrc}
        posterUrl={posterUrl}
        title={title}
        className="courses-hero-player"
      />
      <div className="pt-5">
        <p className="font-body text-[10px] uppercase tracking-[0.28em] text-accent-ink mb-3">{eyebrow}</p>
        <h3 className="font-display text-2xl text-ink mb-3">{title}</h3>
        <p className="font-body text-sm leading-relaxed text-copy/52 mb-5">{copy}</p>
      </div>
    </div>
  );
}

function PublicWebinarPreviewPlayer({ webinar, className = '' }) {
  const iframeRef = useRef(null);
  const [isStarted, setIsStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  if (!hasPublicWebinarPreview(webinar)) {
    return null;
  }

  const provider = sanitizeText(webinar.previewVideoProvider).toLowerCase();
  const embedUrl = sanitizeText(webinar.previewYoutubeEmbedUrl);
  const uploadedVideoUrl = sanitizeText(webinar.previewVideoUrl);
  const playerUrl = getYouTubePreviewPlayerUrl(embedUrl, isStarted);
  const coverImageUrl = sanitizeText(webinar.previewCoverImageUrl);
  const previewTitle = sanitizeText(webinar.previewVideoTitle) || `${sanitizeText(webinar.title) || 'Webinar'} preview`;
  const durationLabel = formatPreviewDurationLabel(webinar.previewVideoDurationSeconds);

  if (provider === 'upload') {
    return (
      <div data-testid="webinar-preview-player" className={`min-w-0 ${className}`}>
        <PublicVideoPlayer
          src={uploadedVideoUrl}
          posterUrl={coverImageUrl}
          title={previewTitle}
          className="border-gold/20"
        />
        <div className="border-x border-b border-gold/14 bg-canvas/70 px-4 py-3">
          <p className="font-body text-[10px] uppercase tracking-[0.22em] text-accent-ink/80">{durationLabel}</p>
          <p className="mt-1 font-body text-sm leading-snug text-copy/72">{previewTitle}</p>
        </div>
      </div>
    );
  }

  const handlePlayPause = () => {
    if (!isStarted) {
      setIsStarted(true);
      setIsPlaying(true);
      window.setTimeout(() => postYouTubePreviewCommand(iframeRef.current, embedUrl, 'playVideo'), 250);
      return;
    }
    const nextPlaying = !isPlaying;
    postYouTubePreviewCommand(iframeRef.current, embedUrl, nextPlaying ? 'playVideo' : 'pauseVideo');
    setIsPlaying(nextPlaying);
  };

  const handleMuteToggle = () => {
    const nextMuted = !isMuted;
    postYouTubePreviewCommand(iframeRef.current, embedUrl, nextMuted ? 'mute' : 'unMute');
    setIsMuted(nextMuted);
  };

  return (
    <div data-testid="webinar-preview-player" className={`min-w-0 ${className}`}>
      <div data-theme-surface="dark" className="relative aspect-video overflow-hidden border border-gold/20 bg-black">
        {isStarted && playerUrl ? (
          <iframe
            ref={iframeRef}
            className="absolute inset-0 h-full w-full"
            src={playerUrl}
            title={previewTitle}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="autoplay; encrypted-media; picture-in-picture"
          />
        ) : null}
        {!isStarted ? (
          <button data-theme-surface="dark"
            type="button"
            onClick={handlePlayPause}
            className="absolute inset-0 group overflow-hidden bg-canvas text-left"
            aria-label={`Play ${previewTitle}`}
          >
            {coverImageUrl ? (
              <img src={coverImageUrl} alt="" className="h-full w-full object-cover opacity-82 transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-gold/[0.16] via-canvas to-black" />
            )}
            <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-black/35" />
            <span className="absolute left-3 top-3 border border-ink/14 bg-canvas/72 px-3 py-1 font-body text-[10px] uppercase tracking-[0.2em] text-copy/72">
              {durationLabel}
            </span>
            <span className="absolute left-1/2 top-1/2 inline-flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold/40 bg-gold text-obsidian shadow-[0_0_32px_rgba(76,141,204,0.18)] transition-transform group-hover:scale-105">
              <Play className="h-5 w-5 fill-current" />
            </span>
          </button>
        ) : null}
        {isStarted ? (
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3">
            <button type="button" onClick={handlePlayPause} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 bg-canvas/82 text-accent-ink backdrop-blur hover:bg-gold hover:text-obsidian transition-colors" aria-label={isPlaying ? 'Pause preview' : 'Play preview'}>
              {isPlaying ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
            </button>
            <button type="button" onClick={handleMuteToggle} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 bg-canvas/82 text-copy/74 backdrop-blur hover:border-gold/50 hover:text-accent-ink transition-colors" aria-label={isMuted ? 'Unmute preview' : 'Mute preview'}>
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
          </div>
        ) : null}
      </div>
      <div className="border-x border-b border-gold/14 bg-canvas/70 px-4 py-3">
        <p className="font-body text-[10px] uppercase tracking-[0.22em] text-accent-ink/80">{durationLabel}</p>
        <p className="mt-1 font-body text-sm leading-snug text-copy/72">{previewTitle}</p>
      </div>
    </div>
  );
}

function WebinarSeriesCard({ webinar, copiedCardId, highlightedCardId, onWatch, onShare, bundleMode = false, onBundleApply }) {
  const presentation = resolveCourseDetailPresentation(webinar);
  const highlighted = highlightedCardId === webinar.anchor;
  const payment = getWebinarPaymentConfig(webinar);
  const hasPreview = hasPublicWebinarPreview(webinar);
  const handlePrimaryAction = () => {
    if (bundleMode && onBundleApply) {
      onBundleApply();
      return;
    }
    onWatch(webinar.registrationId || webinar.anchor);
  };
  return (
    <section id={webinar.anchor} data-course-presentation="card" data-theme={presentation.key}
      style={{ '--course-accent': presentation.accent, '--course-secondary': presentation.secondaryAccent || presentation.accent, ...(highlighted ? { borderColor: `${presentation.accent}cc` } : {}) }}
      className={`courses-session-card course-presentation-card webinar-card scroll-mt-28 reveal visible ${highlighted ? 'is-highlighted' : ''}`}>
      <header className="courses-session-heading">
        <div className="courses-session-number"><p>{webinar.number}</p><p>Recorded course</p></div>
        <div className="courses-session-title"><h3>{webinar.title}</h3><p>{webinar.shortText}</p></div>
        <div className="courses-session-category">
          <p>{COURSE_CATEGORIES.find(({ id }) => id === webinar.category)?.label || 'Course'}</p>
          <p>{webinar.isDemo ? 'Demo outline' : 'Course outline'}</p>
          <p>Recorded videos with accompanying learning material.</p>
        </div>
      </header>
      <div className={'courses-session-content' + (hasPreview ? ' courses-session-content--preview' : '')}>
        {hasPreview && <PublicWebinarPreviewPlayer webinar={webinar} />}
        <div className="courses-session-details">
          <div><h4>What it covers</h4><ul>{webinar.covered.map(item => <li key={item}>{item}</li>)}</ul></div>
          <div><h4>Who it is for</h4><p>{webinar.audience}</p></div>
          <div><h4>Learning goal</h4><p>{webinar.takeaway}</p></div>
        </div>
      </div>
      <footer className="courses-session-actions">
        <FlowButton to={getCourseDetailPath(webinar)}>{webinar.isDemo ? 'View demo outline' : "View course details"}</FlowButton>
        <FlowButton type="button" data-share-card={webinar.anchor} onClick={() => onShare(webinar.anchor)}>
          {copiedCardId === webinar.anchor ? 'Link copied' : "Share course"} <Share2 className="w-4 h-4" />
        </FlowButton>
      </footer>
    </section>
  );
}

export default function WebinarsPage() {
  const ref = useReveal();
  const hero = useSiteContent('webinarsHero');
  const promotionPoster = usePublishedHero('coursePromotionPoster');
  const heroParagraphs = splitSiteContentParagraphs(hero.body);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = COURSE_CATEGORIES.some(({ id }) => id === searchParams.get('category')) ? searchParams.get('category') : '';
  const selectCategory = (category) => {
    setIsSeriesExpanded(false);
    setSearchParams((current) => { const next = new URLSearchParams(current); if (category) next.set('category', category); else next.delete('category'); return next; }, { replace: true });
  };
  const paymentConfigRequestRef = useRef(0);
  const seriesSectionRef = useRef(null);

  const [webinars, setWebinars] = useState([]);
  const [webinarsLoaded, setWebinarsLoaded] = useState(false);
  const [webinarsError, setWebinarsError] = useState('');
  const [selectedWebinarId, setSelectedWebinarId] = useState(null);
  const [featuredContent, setFeaturedContent] = useState(DEFAULT_FEATURED_WEBINAR_CONTENT);
  const [copiedCardId, setCopiedCardId] = useState('');
  const [highlightedCardId, setHighlightedCardId] = useState('');
  const [isSeriesExpanded, setIsSeriesExpanded] = useState(false);
  const [seriesParallaxOffset, setSeriesParallaxOffset] = useState(0);

  const [form, setForm] = useState(() => ({ ...EMPTY_WEBINAR_FORM }));
  const [formStep, setFormStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturingPayment, setIsCapturingPayment] = useState(false);
  const [isLoadingPaymentConfig, setIsLoadingPaymentConfig] = useState(false);
  const [selectedPaymentConfig, setSelectedPaymentConfig] = useState(null);
  const [paymentConfigError, setPaymentConfigError] = useState('');
  const [swiftInstructions, setSwiftInstructions] = useState(null);
  const seriesCards = useMemo(() => resolveSeriesCards(webinars).filter((course) => !selectedCategory || course.category === selectedCategory), [webinars, selectedCategory]);
  const scheduledWebinars = []; // Website course outlines have no scheduled enrollment in this phase.
  const seriesBundleReachWebinar = useMemo(() => findReachSeriesBundle(webinars, featuredContent), [webinars, featuredContent]);
  const visibleSeriesCards = useMemo(
    () => (isSeriesExpanded ? seriesCards : seriesCards.slice(0, SERIES_COLLAPSED_COUNT)),
    [isSeriesExpanded, seriesCards]
  );
  const hiddenSeriesCount = Math.max(seriesCards.length - SERIES_COLLAPSED_COUNT, 0);
  const canExpandSeries = hiddenSeriesCount > 0;
  const seriesBundleRegistrationId = seriesBundleReachWebinar?.id || '';

  useEffect(() => {
    let isMounted = true;
    let controller;
    const loadCourses = () => {
      controller?.abort();
      const request = new AbortController();
      controller = request;
      listPublicCourses(request.signal).then((courses) => {
        if (!isMounted || request.signal.aborted) return;
        setWebinars(courses);
        setWebinarsError('');
        setWebinarsLoaded(true);
      }).catch((error) => {
        if (!isMounted || request.signal.aborted) return;
        setWebinarsError(error.message || 'Courses are temporarily unavailable.');
        setWebinarsLoaded(true);
      });
    };
    loadCourses();
    const stop = watchCourseContentChanges(loadCourses);

    getPublicFeaturedWebinarContent().then((loadedContent) => {
      if (!isMounted) {
        return;
      }
      setFeaturedContent(loadedContent);
    });

    return () => {
      isMounted = false;
      stop();
      controller?.abort();
    };
  }, []);

  useEffect(() => {
    const root = ref.current;
    if (!root || !webinarsLoaded) {
      return undefined;
    }

    const revealElements = Array.from(root.querySelectorAll('.reveal:not(.visible)'));
    if (!revealElements.length) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      revealElements.forEach((element) => element.classList.add('visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    revealElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [ref, webinarsLoaded, webinars.length, webinarsError]);

  useEffect(() => {
    if (!isSeriesExpanded) {
      setSeriesParallaxOffset(0);
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setSeriesParallaxOffset(0);
      return undefined;
    }

    let frame = 0;
    const updateOffset = () => {
      frame = 0;
      const element = seriesSectionRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const progress = Math.max(-1, Math.min(1, (viewportHeight / 2 - rect.top) / viewportHeight));
      setSeriesParallaxOffset(Math.round(progress * 18));
    };
    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateOffset);
    };

    updateOffset();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, [isSeriesExpanded]);

  useEffect(() => {
    const scrollToHashCard = () => {
      const hash = sanitizeText(window.location.hash).replace(/^#/, '');
      if (!hash) {
        return;
      }

      const target = document.getElementById(hash);
      if (!target) {
        if (seriesCards.some((webinar) => webinar.anchor === hash)) {
          setIsSeriesExpanded(true);
        }
        return;
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedCardId(hash);
      window.setTimeout(() => setHighlightedCardId((current) => (current === hash ? '' : current)), 3000);
    };

    const timers = [250, 900, 1600].map((delay) => window.setTimeout(scrollToHashCard, delay));
    window.addEventListener('hashchange', scrollToHashCard);
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener('hashchange', scrollToHashCard);
    };
  }, [seriesCards, webinarsLoaded, webinars.length]);

  useEffect(() => {
    if (searchParams.has('webinar_checkout')) navigate('/customer/access', { replace: true });
  }, [navigate, searchParams]);

  const handleRegisterClick = (id) => {
    const course = webinars.find((entry) => entry.id === id || entry.slug === id);
    if (course) navigate(`/webinars/${course.slug}`);
  };

  const handleShareCard = async (cardId) => {
    const url = `${window.location.origin}${window.location.pathname}#${cardId}`;
    try {
      await copyToClipboard(url);
      setCopiedCardId(cardId);
      setHighlightedCardId(cardId);
      document.getElementById(cardId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => setCopiedCardId((current) => (current === cardId ? '' : current)), 1800);
      window.setTimeout(() => setHighlightedCardId((current) => (current === cardId ? '' : current)), 3000);
    } catch (error) {
      toast.error('The course link could not be copied. Please try again.');
    }
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateAgreementAcceptance = (accepted) => {
    setForm((current) => ({
      ...current,
      termsAccepted: accepted,
      agreementAcceptedAt: accepted ? new Date().toISOString() : '',
      agreementVersion: WEBINAR_AGREEMENT_VERSION,
      agreementTitle: WEBINAR_AGREEMENT_TITLE,
      agreementUrl: getWebinarAgreementUrl(),
    }));
  };

  const handleBasicInfoContinue = () => {
    if (!sanitizeText(form.name) || !sanitizeText(form.email)) {
      toast.error('Enter your name and email before continuing.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitizeText(form.email))) {
      toast.error('Enter a valid email address before continuing.');
      return;
    }

    setFormStep(2);
  };

  const validateCompanyInfo = () => {
    const missing = [];
    if (!sanitizeText(form.company)) missing.push('company or organization');
    if (!sanitizeText(form.dotNumber) && !sanitizeText(form.mcNumber)) missing.push('DOT or MC number');
    if (!sanitizeText(form.truckCount)) missing.push('number of trucks');
    if (!sanitizeText(form.employeeCount)) missing.push('number of employees');
    if (!sanitizeText(form.companyZipCode)) missing.push('company ZIP code');

    if (missing.length) {
      toast.error(`Add ${missing.join(', ')} before continuing.`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateCompanyInfo()) {
      return;
    }
    setIsSubmitting(true);

    try {
      if (selectedWebinar && selectedPayment.required) {
        if (isLoadingPaymentConfig) {
          toast.info('Payment settings are still loading. Please try again in a moment.');
          return;
        }
        if (paymentConfigError || !selectedPaymentConfig) {
          toast.error('Payment settings could not be loaded. Please refresh and try again.');
          return;
        }
        if (!selectedPayment.ready) {
          toast.error(selectedPayment.unavailableReason || `${selectedPayment.providerLabel} is not ready for this course yet.`);
          return;
        }
        if (form.termsAccepted !== true) {
          toast.error('Accept the Magellan agreement before starting paid course checkout.');
          return;
        }

        const pendingRegistration = await submitWebinarRegistration(form, selectedWebinar.id, {
          webinarTitle: selectedWebinar.title,
          paymentRequired: true,
          paymentStatus: 'pending',
          paymentProvider: selectedPayment.provider,
          paymentAmount: selectedPayment.amount,
          paymentCurrency: selectedPayment.currency,
        });

        if (!pendingRegistration.success) {
          toast.error('Enrollment failed. Please try again.');
          console.error('Paid webinar pending registration error:', pendingRegistration.error);
          return;
        }

        const registrationReference = extractCarrierFlowReference(pendingRegistration.data);
        const origin = window.location.origin;
        const webinarPath = encodeURIComponent(selectedWebinar.id);

        if (selectedPayment.provider === 'paypal') {
          const orderResult = await createWebinarPayPalOrder({
            form,
            registrationReference,
            paymentConfig: selectedPaymentConfig,
            webinar: {
              ...selectedWebinar,
              paymentAmount: selectedPayment.amount,
              currency: selectedPayment.currency,
              returnUrl: `${origin}/webinars?webinar=${webinarPath}&webinar_checkout=paypal&webinar_result=approved`,
              cancelUrl: `${origin}/webinars?webinar=${webinarPath}&webinar_checkout=paypal&webinar_result=cancelled`,
            },
          });

          if (!orderResult.success) {
            toast.error('PayPal checkout could not be started. Please try again.');
            console.error('Paid webinar PayPal order error:', orderResult.error);
            return;
          }

          const approvalUrl = getPayPalApprovalUrl(orderResult.data);
          if (!approvalUrl || !isTrustedPayPalUrl(approvalUrl)) {
            reportClientError('webinar_paypal_approval_url_untrusted', new Error('PayPal approval URL is not trusted.'), {
              webinarId: selectedWebinar.id,
            });
            toast.error('PayPal checkout is not configured correctly. Please contact support.');
            return;
          }

          writeCheckoutSession({
            provider: 'paypal',
            form,
            webinar: {
              id: selectedWebinar.id,
              title: selectedWebinar.title,
            },
            payment: selectedPayment,
            paymentConfig: selectedPaymentConfig,
            registrationReference,
            orderId: getPayPalOrderReference(orderResult.data),
          });
          window.location.assign(approvalUrl);
          return;
        }

        if (selectedPayment.provider === 'stripe') {
          const stripeResult = await createWebinarStripeCheckoutSession({
            form,
            registrationReference,
            paymentConfig: selectedPaymentConfig,
            webinar: {
              ...selectedWebinar,
              paymentAmount: selectedPayment.amount,
              currency: selectedPayment.currency,
              returnUrl: `${origin}/webinars?webinar=${webinarPath}&webinar_checkout=stripe&webinar_result=approved`,
              cancelUrl: `${origin}/webinars?webinar=${webinarPath}&webinar_checkout=stripe&webinar_result=cancelled`,
            },
          });

          if (!stripeResult.success) {
            toast.error('Stripe checkout could not be started. Please try again.');
            console.error('Paid webinar Stripe checkout error:', stripeResult.error);
            return;
          }

          const checkoutUrl = getCheckoutUrl(stripeResult.data);
          const checkoutSessionId = getCheckoutSessionReference(stripeResult.data);
          if (!checkoutUrl || !checkoutSessionId || !isTrustedStripeUrl(checkoutUrl)) {
            reportClientError('webinar_stripe_checkout_url_untrusted', new Error('Stripe checkout URL is not trusted.'), {
              webinarId: selectedWebinar.id,
            });
            toast.error('Stripe checkout is not configured correctly. Please contact support.');
            return;
          }

          writeCheckoutSession({
            provider: 'stripe',
            form,
            webinar: {
              id: selectedWebinar.id,
              title: selectedWebinar.title,
            },
            payment: selectedPayment,
            paymentConfig: selectedPaymentConfig,
            registrationReference,
            checkoutSessionId,
          });
          window.location.assign(checkoutUrl);
          return;
        }

        if (selectedPayment.provider === 'swift') {
          const swiftResult = await startWebinarSwiftPayment({
            form,
            registrationReference,
            paymentConfig: selectedPaymentConfig,
            webinar: {
              ...selectedWebinar,
              paymentAmount: selectedPayment.amount,
              currency: selectedPayment.currency,
            },
          });

          if (!swiftResult.success) {
            toast.error('SWIFT payment instructions could not be generated. Please try again.');
            console.error('Paid webinar SWIFT start error:', swiftResult.error);
            return;
          }

          setSwiftInstructions(swiftResult.data?.instructions || {
            reference: sanitizeText(swiftResult.data?.reference || registrationReference),
            amount: selectedPayment.amount,
            currency: selectedPayment.currency,
          });
          setSubmitted(true);
          toast.success('SWIFT payment instructions are ready.');
          return;
        }

        toast.error('This course payment method is not available yet.');
        return;
      }

      const result = await submitWebinarRegistration(form, selectedWebinarId, {
        webinarTitle: selectedWebinar?.title,
        paymentRequired: false,
        paymentStatus: 'not_required',
        paymentProvider: 'manual',
        paymentAmount: 0,
        paymentCurrency: 'USD',
      });
      if (result.success) {
        setSubmitted(true);
        toast.success(isInterestOnly ? 'Interest submitted.' : 'Free enrollment confirmed.');
      } else {
        toast.error(isInterestOnly ? 'Interest submission failed. Please try again.' : 'Enrollment failed. Please try again.');
        console.error('Webinar registration error:', result.error);
      }
    } catch (error) {
      toast.error('An unexpected error occurred.');
      console.error('Unexpected error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCalCom = () => {
    if (!WEBINAR_CAL_URL) {
      toast.error('Follow-up booking is not configured yet.');
      return;
    }

    const trustedUrl = buildTrustedWebinarBookingUrl(form);
    if (!trustedUrl) {
      reportClientError('webinar_follow_up_url_untrusted', new Error('Webinar follow-up URL is not trusted.'));
      toast.error('Follow-up booking is not configured correctly yet.');
      return;
    }

    window.open(trustedUrl, '_blank', 'noopener,noreferrer');
  };

  const seriesBundleWebinar = selectedWebinarId === SERIES_BUNDLE_ID && seriesBundleReachWebinar ? {
    id: seriesBundleRegistrationId,
    title: featuredContent?.title || 'Buy all 10 courses',
    description: featuredContent?.description || DEFAULT_FEATURED_WEBINAR_CONTENT.description,
    dateTime: getWebinarScheduleLabel(seriesBundleReachWebinar || featuredContent),
    price: normalizeMoney(seriesBundleReachWebinar?.paymentAmount ?? seriesBundleReachWebinar?.price ?? featuredContent?.price ?? DEFAULT_FEATURED_WEBINAR_CONTENT.price) || 1499,
    regularPrice: normalizeMoney(featuredContent?.regularPrice || DEFAULT_FEATURED_WEBINAR_CONTENT.regularPrice) || 1999,
    currency: sanitizeText(seriesBundleReachWebinar?.paymentCurrency || seriesBundleReachWebinar?.currency || 'USD') || 'USD',
    paymentRequired: true,
    paymentProvider: sanitizeWebinarPaymentProvider(seriesBundleReachWebinar?.paymentProvider, ''),
    status: 'upcoming',
  } : null;
  const selectedWebinar = seriesBundleWebinar
    || seriesCards.find((w) => w.registrationId === selectedWebinarId)
    || webinars.find((w) => w.id === selectedWebinarId)
    || null;
  const selectedPayment = mergePaymentConfigForWebinar(getWebinarPaymentConfig(selectedWebinar), selectedPaymentConfig);
  const isInterestOnly = !selectedWebinar;
  const hasFeaturedContent = Boolean(seriesBundleReachWebinar && sanitizeText(featuredContent?.title));
  const isSwiftPending = Boolean(swiftInstructions);
  const isPaymentSubmitBlocked = Boolean(
    selectedPayment.required
    && (isLoadingPaymentConfig || paymentConfigError || !selectedPayment.ready || form.termsAccepted !== true)
  );
  const submitButtonLabel = getSubmitButtonLabel(selectedPayment, isInterestOnly);
  const submittedTitle = isSwiftPending
    ? 'Payment Instructions Created'
    : isInterestOnly
      ? 'Interest Submitted'
      : 'Enrollment Confirmed';
  const submittedCopy = isSwiftPending
    ? `Your enrollment is pending SWIFT confirmation. Use the reference below and send payment from ${form.email || 'your billing contact'}.`
    : isInterestOnly
      ? `Your course interest has been sent from ${form.email || 'your email'}. We will follow up with upcoming session details.`
      : `You are enrolled. A confirmation has been sent to ${form.email || 'your email'}. Optionally book a 15-minute follow-up call below.`;

  return (
    <div ref={ref} className="courses-page min-h-screen bg-canvas">
      <section data-theme-surface="dark" data-testid="webinars-hero" className={`${WEBINARS_HERO_SECTION_CLASS} site-image-hero courses-hero`}>
        <HeroBackground imageUrl={hero.imageUrl} imageAlt={hero.imageAlt} />
        <div className="absolute inset-0 grain-overlay" />
        <div data-hero-text-parallax="true" className={`${PAGE_HERO_CONTAINER_CLASS} pb-28 md:pb-16`}>
          <div className="courses-hero-layout">
            <div className="courses-hero-text min-w-0 w-full">
              <p className={PAGE_HERO_EYEBROW_CLASS}>{hero.eyebrow}</p>
              <h1 aria-label={hero.title} className={PAGE_HERO_H1_CLASS}>{renderGoldHeroTitle(hero.title)}</h1>
              <div className={`${PAGE_HERO_BODY_TEXT_CLASS} space-y-4`}>
                {heroParagraphs.slice(0, 3).map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
            <VideoPreview
              videoUrl={featuredContent.shortOverviewVideoUrl}
              fullVideoUrl={featuredContent.fullOverviewVideoUrl}
              posterUrl={promotionPoster.imageUrl}
            />
            <div className="courses-hero-actions">
              <SiteHeroCtas
                content={hero}
                className={PAGE_HERO_CTA_ROW_CLASS}
                primaryClassName={HERO_CTA_PRIMARY_CLASS}
                secondaryClassName={HERO_CTA_SECONDARY_CLASS}
                tertiaryClassName={HERO_CTA_SECONDARY_CLASS}
              />
            </div>
          </div>
        </div>
        <HeroScrollRail targetId="webinar-series" />
      </section>

      <div className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...WEBINARS_MARQUEE_ITEMS, ...WEBINARS_MARQUEE_ITEMS].map((item, index) => (
              <span key={`${item}-${index}`} className="inline-flex items-center gap-3 px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24 whitespace-nowrap">
                {item}
                <span className="h-1 w-1 rotate-45 bg-gold/45" />
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="courses-content">
      <WebinarsIntroBand />

      <section className="courses-section courses-training-intro py-16 md:py-20 bg-surface border-y border-ink/6">
        <div className="site-content-shell courses-editorial-split">
          <SectionHeader
            eyebrow="Everyday office tasks"
            title="What needs attention during the working day?"
            copy="An office task often continues with someone else: a collected document still needs checking, and the next dispatcher needs the load update before taking over a shift. The courses explain how to handle those responsibilities and the questions that arise along the way."
          />
          <div className="courses-training-signals reveal reveal-delay-2">
            {SERIES_INTRO_LINES.map((line) => (
              <div key={line} className="border border-ink/8 bg-canvas p-4">
                <p className="font-body text-sm leading-relaxed text-copy/64">{line}</p>
              </div>
            ))}
            <div className="sm:col-span-2 border border-gold/20 bg-gold/[0.045] p-5">
              <p className="font-body text-copy/68 leading-relaxed">
                A collected document can still have missing information or an expiration date that needs attention. The recordkeeping course explains how to check it and follow up on what needs updating.
              </p>
              <Link to="/paperwork" className="mt-4 inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.2em] text-accent-ink hover:text-accent-ink-hover">
                Need policies, procedures and forms? View paperwork packages <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="webinars-upcoming" className="courses-section courses-featured-section py-16 md:py-20">
        <div className="site-content-shell">
          <SectionHeader
            eyebrow="The complete collection"
            title="All ten subjects in one course series."
            copy="The series brings the current ten courses together for $1,499 per person. Buying them individually at $299 each would cost $2,990. The collection suits people whose responsibilities span several areas of the office."
            className="max-w-4xl mb-10"
          />

          <div className="space-y-6">
            {hasFeaturedContent ? (
              <div className="courses-featured-offer reveal border border-gold/20 bg-surface p-7 md:p-8 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-8 items-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-gold/[0.04] via-transparent to-transparent" />
                <div className="relative z-10">
                  <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">{featuredContent.eyebrow}</p>
                  <h3 className="font-display text-3xl text-ink tracking-tight mb-3">{seriesBundleReachWebinar.title}</h3>
                  {featuredContent.subject ? <p className="font-body text-accent-ink/80 text-sm mb-5">{featuredContent.subject}</p> : null}
                  <p className="font-body text-copy/52 leading-relaxed max-w-3xl mb-6">{seriesBundleReachWebinar.description}</p>
                  <div className="flex flex-wrap gap-3">
                    <FlowButton type="button" onClick={() => handleRegisterClick(SERIES_BUNDLE_ID)} className={HERO_CTA_PRIMARY_CLASS}>
                      View series details
                    </FlowButton>
                    <FlowButton as="a" href="#webinar-series" className={HERO_CTA_SECONDARY_CLASS}>
                      Explore individual courses
                    </FlowButton>
                  </div>
                </div>
                <div className="relative z-10 border border-ink/10 bg-canvas p-5">
                  <p className="font-body text-[10px] uppercase tracking-[0.24em] text-copy/38 mb-3">Recorded course collection</p>
                  <p className="font-body text-sm text-copy/70 mb-5">All {seriesBundleReachWebinar.bundleCourseIds?.length || webinars.filter((course) => course.kind !== 'bundle').length} courses</p>
                  <p className="font-display text-2xl text-accent-ink leading-none">$1,499 per person</p>
                  <p className="mt-4 font-body text-xs leading-relaxed text-copy/52">Three months for one person from receipt of payment. Recorded videos include PDF notes, checklists, forms or worked examples, as applicable to each subject. Enrollment and payment are not enabled for demo courses.</p>
                </div>
              </div>
            ) : null}

            {!webinarsLoaded ? (
              <div className="flex items-center gap-3 text-copy/48">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="font-body text-sm">Loading courses…</span>
              </div>
            ) : webinarsError ? (
              <div className="reveal border border-amber-600/30 p-8 max-w-xl">
                <CalendarDays className="w-10 h-10 text-amber-400/70 mb-4" />
                <p className="font-display text-ink text-xl mb-3">Courses are temporarily unavailable.</p>
                <p className="font-body text-copy/50 text-sm leading-relaxed mb-6">
                  {webinarsError}
                </p>
                <FlowButton as="a" href="/#contact" className={HERO_CTA_SECONDARY_CLASS}>
                  Contact us
                </FlowButton>
              </div>
            ) : scheduledWebinars.length === 0 ? (
              <div className="reveal border border-ink/9 bg-surface p-8 max-w-xl">
                <CalendarDays className="w-10 h-10 text-accent-ink/70 mb-4" />
                <p className="font-display text-ink text-xl mb-3">One course for a specific responsibility.</p>
                <p className="font-body text-copy/50 text-sm leading-relaxed mb-6">
                  You can study a single subject without buying the full series. The driver-file course covers checking collected documents and following up on missing information. Driver onboarding focuses on company instruction and later refreshers.
                </p>
                <FlowButton as="a" href="#webinar-series" className={HERO_CTA_SECONDARY_CLASS}>
                  Explore courses
                </FlowButton>
              </div>
            ) : (
              <div className="grid gap-5">
                {scheduledWebinars.map((webinar) => {
                  const payment = getWebinarPaymentConfig(webinar);
                  const cardAnchor = getPublicWebinarAnchor(webinar);
                  const highlighted = highlightedCardId === cardAnchor;
                  const hasPreview = hasPublicWebinarPreview(webinar);
                  return (
                    <div
                      key={webinar.id}
                      id={cardAnchor}
                      className={`webinar-card scroll-mt-28 reveal visible border bg-surface p-7 md:p-8 grid grid-cols-1 gap-8 items-center relative overflow-hidden transition-all duration-300 ${hasPreview ? 'lg:grid-cols-[minmax(260px,340px)_1fr_260px]' : 'lg:grid-cols-[1fr_260px]'} ${highlighted ? 'is-highlighted border-gold/80' : 'border-gold/20'}`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-gold/[0.04] via-transparent to-transparent" />
                      {hasPreview ? (
                        <PublicWebinarPreviewPlayer webinar={webinar} className="relative z-10" />
                      ) : null}
                      <div className="relative z-10">
                        <div className="flex flex-wrap items-center gap-3 mb-5">
                          <span className={`text-xs font-body px-2 py-0.5 rounded-full border ${
                            webinar.status === 'live' ? 'text-emerald-400 border-emerald-400/30 bg-emerald-400/5' : 'text-accent-ink border-gold/30 bg-gold/5'
                          }`}>
                            {webinar.status === 'live' ? 'Live Now' : 'Upcoming'}
                          </span>
                          <span className="text-xs font-body px-2 py-0.5 rounded-full border border-ink/15 text-copy/68 bg-ink/[0.03]">
                            Current offer at checkout
                          </span>
                        </div>
                        <h3 className="font-display text-3xl text-ink mb-4">{webinar.title}</h3>
                        <div className="mb-6 inline-flex items-start gap-3 border border-gold/20 bg-gold/[0.04] px-5 py-4">
                          <CalendarDays className="w-5 h-5 text-accent-ink mt-0.5" />
                          <div>
                            <p className="font-body text-[10px] uppercase tracking-[0.22em] text-accent-ink/75 mb-1">Date and time</p>
                            <p className="font-body text-sm text-copy/74">{getWebinarScheduleLabel(webinar)}</p>
                          </div>
                        </div>
                        {webinar.description ? <p className="font-body text-copy/52 leading-relaxed max-w-3xl mb-7">{webinar.description}</p> : null}
                        <div className="flex flex-col sm:flex-row gap-3">
                          <FlowButton type="button" onClick={() => handleRegisterClick(webinar.id)} className={HERO_CTA_PRIMARY_CLASS}>
                            Buy Individual Course
                          </FlowButton>
                          <FlowButton to={getCourseDetailPath(webinar)} className={HERO_CTA_SECONDARY_CLASS}>
                            Course Details
                          </FlowButton>
                          <FlowButton type="button" data-share-card={cardAnchor} onClick={() => handleShareCard(cardAnchor)} className={HERO_CTA_SECONDARY_CLASS}>
                            {copiedCardId === cardAnchor ? 'Link copied' : 'Share Link'} <Share2 className="w-4 h-4" />
                          </FlowButton>
                        </div>
                      </div>
                      <div className="relative z-10 border border-ink/10 bg-canvas p-5">
                        <p className="text-xs uppercase tracking-[0.25em] text-copy/38 font-body mb-3">Price</p>
                        <p className="font-display text-2xl text-accent-ink leading-none">Current offer at checkout</p>
                        <div className="mt-6 border-t border-ink/10 pt-5">
                          <p className="text-xs uppercase tracking-[0.25em] text-copy/38 font-body mb-2">Date and time</p>
                          <p className="font-body text-copy/58 text-sm leading-relaxed">{getWebinarScheduleLabel(webinar)}</p>
                        </div>
                        <p className="mt-3 font-body text-emerald-300/80 text-xs uppercase tracking-[0.18em]">
                          Review current access in your account
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="webinar-series" data-testid="webinars-series" ref={seriesSectionRef} className="courses-section courses-library py-16 md:py-20 bg-surface">
        <div className="site-content-shell">
          <SectionHeader
            eyebrow="Individual courses"
            title={selectedCategory ? `${COURSE_CATEGORIES.find(({ id }) => id === selectedCategory).label} courses` : 'Choose one subject for $299 per person.'}
            copy="Compare the topics and learning goals in the cards below. Each course has a defined focus, from document checks and driver instruction to dispatch handoffs or management review."
            className="max-w-4xl mb-10"
          />
          <div className="mb-8 flex flex-wrap gap-x-8 gap-y-4 border-b border-ink/10 pb-6" role="group" aria-label="Course categories">
            {[{ id: '', label: 'All' }, ...COURSE_CATEGORIES].map((category) => (
              <button key={category.id || 'all'} type="button" aria-pressed={selectedCategory === category.id} onClick={() => selectCategory(category.id)}
                className={`border-b pb-2 text-[11px] uppercase tracking-[0.22em] font-body transition-colors ${selectedCategory === category.id ? 'border-gold text-accent-ink' : 'border-transparent text-copy/48 hover:border-gold/40 hover:text-accent-ink'}`}>
                {category.label}{webinarsLoaded && !webinarsError ? <span className="ml-2 text-copy/38">{webinars.filter((course) => course.kind !== 'bundle' && (!category.id || course.category === category.id)).length}</span> : null}
              </button>
            ))}
          </div>
          <div className="courses-library-toolbar mb-8 border border-gold/18 bg-canvas p-5 md:p-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-5 items-center">
            <div>
              <p className="font-body text-[10px] uppercase tracking-[0.28em] text-accent-ink/80 mb-3">Course selection</p>
              <p className="font-body text-copy/60 leading-relaxed max-w-3xl">
                {seriesCards.length
                  ? `Showing the first ${Math.min(SERIES_COLLAPSED_COUNT, seriesCards.length)} of ${seriesCards.length} courses.`
                  : "The course library is organized by category."}
                {" Expand the selection to see the remaining subjects."}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3">
              {seriesBundleReachWebinar ? (
                <FlowButton type="button" onClick={() => handleRegisterClick(SERIES_BUNDLE_ID)} className={HERO_CTA_PRIMARY_CLASS}>
                  View complete series
                </FlowButton>
              ) : null}
              {canExpandSeries ? (
                <FlowButton
                  type="button"
                  onClick={() => setIsSeriesExpanded((current) => !current)}
                  aria-expanded={isSeriesExpanded}
                  aria-controls="webinar-series-list"
                  className={HERO_CTA_SECONDARY_CLASS}
                >
                  {isSeriesExpanded ? "Show first 3 courses" : `View all ${seriesCards.length} courses`}
                  {isSeriesExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </FlowButton>
              ) : null}
            </div>
          </div>
          <div id="webinar-series-list" className="space-y-5">
            {visibleSeriesCards.length ? visibleSeriesCards.map((webinar, index) => {
              const isExpandedExtra = isSeriesExpanded && index >= SERIES_COLLAPSED_COUNT;
              const extraIndex = Math.max(0, index - SERIES_COLLAPSED_COUNT);
              const parallaxTranslate = Math.max(0, seriesParallaxOffset - extraIndex * 4);
              return (
                <div
                  key={webinar.anchor}
                  className="transition-all duration-500 ease-out motion-reduce:transition-none motion-reduce:transform-none"
                  style={isExpandedExtra ? { transform: `translateY(${parallaxTranslate}px)` } : undefined}
                >
                  <WebinarSeriesCard
                    webinar={webinar}
                    copiedCardId={copiedCardId}
                    highlightedCardId={highlightedCardId}
                    onWatch={handleRegisterClick}
                    onShare={handleShareCard}
                    bundleMode={Boolean(seriesBundleReachWebinar)}
                    onBundleApply={seriesBundleReachWebinar ? () => handleRegisterClick(SERIES_BUNDLE_ID) : undefined}
                  />
                </div>
              );
            }) : (
              <div className="border border-ink/9 bg-canvas p-8">
                <CalendarDays className="mb-4 h-10 w-10 text-accent-ink/70" />
                <h3 className="font-display text-2xl text-ink mb-3">{!webinarsLoaded ? 'Loading courses…' : webinarsError ? 'Courses are temporarily unavailable.' : 'No courses are published in this category.'}</h3>
                <p className="font-body text-sm leading-relaxed text-copy/50">
                  {!webinarsLoaded ? 'The current course library is loading.' : webinarsError ? webinarsError : 'Choose another category to explore the library.'}
                </p>
              </div>
            )}
          </div>
          {!isSeriesExpanded && canExpandSeries ? (
            <div className="mt-5 border border-ink/9 bg-canvas/70 p-5 text-center">
              <p className="font-body text-sm text-copy/54">
                {hiddenSeriesCount} more courses in this selection.
              </p>
              <FlowButton
                type="button"
                onClick={() => setIsSeriesExpanded(true)}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-gold/30 px-5 py-3 font-body text-sm  transition-colors "
              >
                View remaining courses <ChevronDown className="w-4 h-4" />
              </FlowButton>
            </div>
          ) : null}
          {isSeriesExpanded && canExpandSeries ? (
            <div className="mt-7 flex justify-center">
              <FlowButton
                type="button"
                onClick={() => setIsSeriesExpanded(false)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/14 px-5 py-3 font-body text-sm text-copy/60 transition-colors hover:border-gold/40 "
              >
                Back to first 3 courses <ChevronUp className="w-4 h-4" />
              </FlowButton>
            </div>
          ) : null}
        </div>
      </section>

      <section id="webinar-register-form" data-testid="webinars-form" className="courses-section courses-academy py-16 md:py-20 bg-surface border-y border-ink/6">
        <div className="site-content-shell">
          <div className="grid grid-cols-1 lg:grid-cols-[0.86fr_1.14fr] gap-12 lg:gap-16">
            <div><p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-6">Courses</p>
              <h2 className="font-display text-3xl md:text-4xl text-ink leading-[1.08] mb-6">Learn with recorded videos and supporting material.</h2>
              <p className="font-body text-copy/52 leading-relaxed mb-8">Study the videos alongside the accompanying PDF notes, checklists, forms or worked examples for that subject. All ten courses are recorded, including those in the Webinars category.</p>
              <FlowButton as="a" href="#webinar-series" className={HERO_CTA_PRIMARY_CLASS}>Explore course library </FlowButton>
            </div>
            <div className="border border-ink/10 bg-canvas p-7">
              <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-5">Browse by category</p>
              {COURSE_CATEGORIES.map((category) => <a key={category.id} href={`?category=${category.id}#webinar-series`} className="flex items-center justify-between gap-3 border-b border-ink/10 py-4 font-display text-2xl text-ink hover:text-accent-ink">{category.label}<ArrowRight className="h-4 w-4" /></a>)}
            </div>
          </div>
        </div>
      </section>

      <section className="courses-section courses-starting-guide py-16 md:py-20">
        <div className="site-content-shell">
          <SectionHeader
            eyebrow="Subject guide"
            title="Ten subjects, each with a defined focus."
            copy="Some subjects are closely related but teach different responsibilities. Management review brings unresolved items into view; the process-mapping course examines how a process crosses departments. The summaries show the focus of each."
            className="max-w-4xl mb-10"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {STARTING_GUIDE.map(([title, copy]) => (
              <div key={title} className="reveal flex items-start gap-4 border border-ink/8 bg-surface p-5">
                <ArrowRight className="w-4 h-4 text-accent-ink mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-display text-xl text-ink mb-2">{title}</h3>
                  <p className="font-body text-sm leading-relaxed text-copy/52">{copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="courses-section courses-disclaimer py-14 md:py-16 bg-surface border-y border-ink/6">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-[0.75fr_1.25fr] gap-10 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">Practical disclaimer</p>
            <h2 className="font-display text-3xl text-ink tracking-tight">Practical training, not legal advice</h2>
          </div>
          <p className="font-body text-copy/54 leading-relaxed">
            Magellan courses are built to help trucking companies understand operating structure, documentation habits, workflow issues, training needs, and management visibility. They are not legal advice and do not replace qualified legal, regulatory, insurance, or professional guidance. Use the sessions as practical training for organizing the work inside the company.
          </p>
        </div>
      </section>

      </div>
      <section data-theme-surface="dark" className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-45" style={{ backgroundImage: 'url(/images/webinars/webinars-road-truck.png)' }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-canvas via-canvas/88 to-canvas/56" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="site-content-shell relative z-10 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">Your next step</p>
            <h2 className="font-display text-3xl sm:text-5xl text-ink tracking-tight leading-[1.02] max-w-3xl mb-6">Make time for the subjects you need.</h2>
            <p className="font-body text-copy/58 leading-relaxed max-w-3xl">
              Access runs for three months from the moment Magellan receives payment, whether you buy one course or the current ten-course series. Plan your study within that period. Resources offers separate free reference material.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
            <FlowButton to="/webinars#webinar-series" className={HERO_CTA_PRIMARY_CLASS}>Explore courses </FlowButton>
            <FlowButton to="/resources" className={HERO_CTA_SECONDARY_CLASS}>Explore resources </FlowButton>
          </div>
        </div>
      </section>

    </div>
  );
}
